import { useEffect, useState } from 'react';

const BAR_PREVIEW_IDLE_HOLD_MS = 5000;
const BAR_PREVIEW_QUESTION_HOLD_MS = 2000;

export interface BarPreviewCycle {
  slots: string[];
  activeIndex: number;
  isInstantJump: boolean;
}

/** `enableCycling` is a kill switch, not a removal - the cycling is disabled for now (see
 * `SunnyChat`'s own `enableQuestionPreview` prop), but the slot/timer machinery stays intact
 * and vestigial so it can be flipped back on later without rebuilding it. With it off, this
 * just holds on `slots[0]` (the idle placeholder) forever, same as it already does between
 * cycles. */
export function useBarPreviewCycle(isActive: boolean, questions: string[], idlePlaceholder: string, enableCycling = true): BarPreviewCycle {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInstantJump, setIsInstantJump] = useState(false);
  const duplicateIdleIndex = questions.length + 1;
  const slots = [idlePlaceholder, ...questions, idlePlaceholder];

  useEffect(() => {
    if (!isActive || !enableCycling || questions.length === 0) {
      setActiveIndex(0);
      setIsInstantJump(false);
      return undefined;
    }

    const isDuplicateIdleSlot = activeIndex === duplicateIdleIndex;
    const isRealIdleSlot = activeIndex === 0;
    const holdDuration = isDuplicateIdleSlot ? 0 : isRealIdleSlot ? BAR_PREVIEW_IDLE_HOLD_MS : BAR_PREVIEW_QUESTION_HOLD_MS;

    const timer = setTimeout(() => {
      if (activeIndex === duplicateIdleIndex) {
        setIsInstantJump(true);
        setActiveIndex(0);
      } else {
        setActiveIndex((current) => current + 1);
      }
    }, holdDuration);
    return () => clearTimeout(timer);
  }, [isActive, enableCycling, questions.length, activeIndex, duplicateIdleIndex]);

  useEffect(() => {
    if (!isInstantJump) {
      return undefined;
    }
    const frame = requestAnimationFrame(() => setIsInstantJump(false));
    return () => cancelAnimationFrame(frame);
  }, [isInstantJump]);

  return { slots, activeIndex, isInstantJump };
}
